import { Head, Link } from "@inertiajs/react";
import { ClipboardList, Plus, Trophy } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import RewardManager from "@/components/schools/rewards/RewardManager";
import SuggestedRewards from "@/components/schools/rewards/SuggestedRewards";
import { suggestSchoolRewards } from "@/lib/campaignPlanning";
import { formatMoney, pluralize } from "@/lib/format";

/**
 * School rewards: prizes the whole school earns together as it crosses
 * fundraising or reading milestones, and top-classroom prizes for the
 * leading class in each division. Each row shows how close the school is
 * (or who's leading); an empty list offers rewards sized to the
 * campaign's goals.
 */
export default function Milestones({ school, membership, campaign, milestones, create_path, suggestions_path, disbursements_path }) {
  const hasTopClassroom = milestones.some((m) => m.award_style === "top_classroom");
  const goals = { money_goal_cents: campaign.money_goal_cents, minute_goal: campaign.minute_goal };
  const suggestions = suggestSchoolRewards({ moneyGoalCents: campaign.money_goal_cents, minuteGoal: campaign.minute_goal });
  const goalText = [
    campaign.money_goal_cents && formatMoney(campaign.money_goal_cents),
    campaign.minute_goal && `${campaign.minute_goal.toLocaleString("en-CA")} minutes`,
  ].filter(Boolean).join(" and ");

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign.name}
      title="School rewards"
      actions={hasTopClassroom && disbursements_path && (
        <Button variant="outline" size="sm" asChild>
          <Link href={disbursements_path}>
            <ClipboardList className="h-4 w-4" />
            Prize report
          </Link>
        </Button>
      )}
    >
      <Head title={`School rewards · ${campaign.name}`} />

      <div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Rewards the whole school earns
            </CardTitle>
            <CardDescription>
              When the school's fundraising or reading total reaches a reward, every student earns it. They're shown on the dashboard, donation pages and take-home sheets, listed smallest first. A Top classroom prize goes to the class that reads or raises the most instead, in each division or across the whole school.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RewardManager
              rewards={milestones}
              scope="campaign"
              createPath={create_path}
              locked={campaign.locked}
              showProgress
              goals={goals}
              addLabel="Add a reward"
              removeWarning={removeWarning}
              emptyState={({ onAdd }) => (
                <div className="space-y-4">
                  {suggestions_path && suggestions.length > 0 ? (
                    <SuggestedRewards
                      title={`Rewards sized to your ${goalText} goal`}
                      description="A quick first win, a halfway treat, and a big finish when you hit the goal."
                      suggestions={suggestions}
                      appliesTo="campaign"
                      path={suggestions_path}
                      returnTo="milestones"
                      noun="reward"
                      secondaryAction={<Button variant="ghost" onClick={onAdd}>I'll add my own</Button>}
                    />
                  ) : (
                    <>
                      <p className="pt-2 text-center text-sm text-muted-foreground">
                        No rewards yet. Try a quick first win, a halfway treat and a big finish at your goal.
                      </p>
                      <div className="text-center">
                        <Button onClick={onAdd}>
                          <Plus className="h-4 w-4" />
                          Add your first reward
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

// Removing a top-classroom prize takes its recorded winners with it.
function removeWarning(reward) {
  if (!reward.won_count) return null;
  const handedOut = reward.handed_out_count ? ` (${reward.handed_out_count} already handed out)` : "";
  return `${pluralize(reward.won_count, "class", "classes")} won it${handedOut}. Removing it deletes that record too.`;
}
